// Каркас пульта для вошедшего (задача D5, ui-spec §2 и §6): подложка 8 px, слева сайдбар (или верхняя полоса уже 1024),
// справа белая рабочая панель `#main` с экраном раздела.
//
// Здесь же живёт всё общее для экранов: «К содержимому», баннер «Нет связи», команды ⌘K, шпаргалка «?», тосты,
// окно «Сессия истекла», полоса перехода, фокус на `h1` после перехода и поток событий (пока выключен до D3).
// Клавиши «Везде» (Ctrl K, ?) — отсюда; клавиши экрана — у экрана (`useHotkeys(scope)`).
import { useState, type ReactNode } from 'react';
import { useRole } from '@/api/queries/auth';
import { useStream } from '@/api/stream';
import { SessionExpiredDialog } from '@/auth/session-expired-dialog';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useHotkeys } from '@/lib/hotkeys';
import { isNarrow, useViewport } from '@/lib/viewport';
import { CommandPalette } from './command-palette';
import { MobileTopbar } from './mobile-topbar';
import { NavProgress } from './nav-progress';
import { OfflineBanner } from './offline-banner';
import { RouteFocus } from './route-focus';
import { ShortcutsDialog } from './shortcuts-dialog';
import { Sidebar } from './sidebar';
import { SkipLink } from './skip-link';
import { AppToaster } from './toaster';
import { TopbarSlotProvider } from './topbar-slot';

export function AppShell({ children }: { readonly children: ReactNode }) {
  const role = useRole();
  const viewport = useViewport();
  const narrow = isNarrow(viewport);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useStream();
  useHotkeys('global', {
    command: () => {
      setShortcutsOpen(false);
      setPaletteOpen((open) => !open);
    },
    shortcuts: () => {
      openShortcuts();
    },
  });

  const openPalette = (): void => {
    setPaletteOpen(true);
  };
  const openShortcuts = (): void => {
    setPaletteOpen(false);
    setShortcutsOpen(true);
  };

  return (
    <TooltipProvider>
      <TopbarSlotProvider>
        <SkipLink />
        <NavProgress />
        <div className="flex min-h-dvh gap-2 bg-background p-2">
          {!narrow && <Sidebar compact={viewport === 'compact'} onOpenPalette={openPalette} onOpenShortcuts={openShortcuts} />}
          <div className="flex min-w-0 flex-1 flex-col rounded-xl border border-border bg-card">
            {narrow && <MobileTopbar onOpenPalette={openPalette} onOpenShortcuts={openShortcuts} />}
            <OfflineBanner />
            <main id="main" tabIndex={-1} className="min-w-0 flex-1 outline-none">
              {children}
            </main>
          </div>
        </div>
        {role !== undefined && (
          <>
            <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} role={role} />
            <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} role={role} />
          </>
        )}
        <AppToaster />
        <SessionExpiredDialog />
        <RouteFocus />
      </TopbarSlotProvider>
    </TooltipProvider>
  );
}
